/**
 * Hand-rolled Authorization Code + PKCE against Keycloak (realm `storeo`, client
 * `storeo-painel`, public — já provisionado em `infra/keycloak/storeo-realm.json`). Mesmo
 * padrão do `storefront/lib/pkce.ts` (M4b, ADR 0017): sem `keycloak-js`. `sessionStorage` só —
 * a sessão do painel cai com a aba (ADR 0019 §1).
 */

const BASE = import.meta.env.VITE_KEYCLOAK_BASE_URL ?? 'http://localhost:8081';
const REALM = import.meta.env.VITE_KEYCLOAK_REALM ?? 'storeo';
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT ?? 'storeo-painel';

const ISSUER = `${BASE}/realms/${REALM}`;
const VERIFIER_KEY = 'storeo:painel:pkce-verifier';
const TOKEN_KEY = 'storeo:painel:token';

function base64url(bytes: Uint8Array): string {
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomVerifier(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(32)));
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

export async function startLogin(): Promise<void> {
  const verifier = randomVerifier();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const challenge = await challengeFor(verifier);

  const url = new URL(`${ISSUER}/protocol/openid-connect/auth`);
  url.searchParams.set('client_id', CLIENT_ID);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', window.location.origin + '/');
  url.searchParams.set('scope', 'openid email');
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.assign(url.toString());
}

export async function finishLogin(code: string): Promise<void> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('Sessão de login perdida — tente entrar de novo');

  const res = await fetch(`${ISSUER}/protocol/openid-connect/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code,
      redirect_uri: window.location.origin + '/',
      code_verifier: verifier,
    }),
  });
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!res.ok) throw new Error('Não foi possível entrar — tente de novo');
  const body = (await res.json()) as { access_token: string };
  sessionStorage.setItem(TOKEN_KEY, body.access_token);
}

export function currentToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function logout(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

export interface TokenClaims {
  name?: string;
  email?: string;
  exp?: number;
  realm_access?: { roles?: string[] };
}

/** Só pra UI (nome/e-mail/expiração) — a validação de verdade é sempre do lado da API. */
export function decodeClaims(token: string): TokenClaims | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as TokenClaims;
  } catch {
    return null;
  }
}

export function isExpired(claims: TokenClaims | null): boolean {
  if (!claims?.exp) return true;
  return Date.now() >= claims.exp * 1000;
}
